import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { trpc } from "../lib/trpc";
import {
  MrpUyarilari, mrpEldeNotlari, mrpHesabiEksik, yeterliMetni, MRP_ELDE_TANIMI, MRP_KAYIT_ACIGI_TANIMI,
  type MrpEldeUyarisi,
} from "../components/mrp/MrpUyarilari";
// Durum etiketi Siparişler ekranı / MRP seçicisi / Teslimat Planı ile TEK kaynaktan.
import { durumEtiketi } from "../lib/siparisDurum";

/**
 * MRP RAPORU — seçili siparişler için A4 baskı sayfası.
 *
 * Kullanıcı isteği (29 Ağu 2026): "kullanıcı tekli veya çoklu seçim yaparak seçili siparişlere
 * özel MRP çalıştırsın ve MRP raporunu yazdırabilsin; raporda en üstte siparişler ve kalemleri
 * listelensin, onun altında eksik olan kalemleri sipariş bazında ve toplu olarak listelesin."
 *
 * Sipariş seçimi MRP ekranından query string ile gelir (`?orderIds=a,b,c`) — Teslimat Planı ve
 * Üretim Raporu ile aynı kalıp: sayfa bağımsız açılır, kendi verisini çeker, kendi başlığını basar.
 *
 * ⚠ TOPLU EKSİK, SİPARİŞ BAZINDAKİLERİN TOPLAMI DEĞİLDİR. Stok tüm siparişlerce PAYLAŞILIR:
 * elde 150 kg un varken iki siparişin her biri 100 kg isterse tek tek eksik YOKTUR, birlikte
 * 50 kg eksiktir. Bu yüzden toplu tablo AYRI bir MRP koşusudur ve satın alma kararı ONDAN
 * verilir. Rapor bunu okuyucuya AÇIKÇA yazar — yoksa iki tablo çelişkili okunur.
 *
 * ⚠ Sabit hex paleti bilinçli (diğer baskı sayfalarıyla aynı): belge teması ekran temasına bağlı olmamalı.
 *
 * ⚠ TİP SÖZLEŞMESİ: aşağıdaki `Rapor`/`Bicim` arayüzleri bu sayfanın OKUDUĞU alanlardır;
 * `order.materialNeedsMulti` çıktısı onlara CAST'SİZ atanır. Sunucu bir alanı yeniden adlandırır ya da
 * kaldırırsa tsc atama satırında yakalar (eskiden `as unknown as` kaymayı sessizce yutuyordu).
 */

const nf = (v: number | null | undefined, dec = 0) =>
  v == null ? "—" : new Intl.NumberFormat("tr-TR", { minimumFractionDigits: dec, maximumFractionDigits: 2 }).format(v);

function fmtTarih(d: string | Date | null): string {
  if (!d) return "—";
  const t = typeof d === "string" ? new Date(d) : d;
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(t);
}

interface EksikSatir {
  itemId: string; code: string; name: string; uom: string;
  /** `elde` = KULLANILABİLİR NET stok (mrp.ts `eldeHaritasi`); sktGecmis/karantina/kayitAcigi onun DIŞINDA. */
  brut: number; elde: number; acikPo: number; eksik: number;
  sktGecmis: number; karantina: number; kayitAcigi: number;
  tedarikciAd: string | null; tedarikSuresiGun: number | null;
}
interface Uyari { itemKind: string; itemId: string; code: string; name: string; qty: number }
interface Bicim {
  satirlar: EksikSatir[];
  recetesizUrunler: Uyari[];
  atlananKalemler: Uyari[];
  patlatilamayanBilesenler: Array<Uyari & { uom: string; recetesiVar: boolean }>;
  /** MRP dışı kart türündeki (gider/hizmet) ham maddeler — eksik sayılmadı, AYRICA yazılır. */
  mrpDisiKalemler: Array<{ itemId: string; code: string; name: string; uom: string; qty: number; tur: string }>;
  /** Eksiği OLMAYAN ama SKT geçmiş / karantina stoğu ya da kayıt açığı olan kalemler — "Eksik yok ama dikkat". */
  eldeUyarilari: MrpEldeUyarisi[];
  hesaplananKalem: number;
}
interface Rapor {
  yetkiYok: boolean;
  /** İPTAL oldukları için talebe GİRMEYEN siparişler — raporda açıkça yazılır. */
  kapsamDisiSiparisler: Array<{ id: string; code: string; durum: string }>;
  siparisler: Array<{
    id: string; code: string; musteri: string; durum: string;
    teslimTarihi: string | null; teslimTahmini: boolean;
    /** `mrpMiktar` ÜRÜN bazındadır → aynı ürünün ikinci birim satırında `null` gelir. */
    kalemler: Array<{ code: string; name: string; uom: string; siparisMiktar: number; mrpMiktar: number | null }>;
  }>;
  perSiparis: Array<{ orderId: string; code: string; musteri: string; talepYok: boolean; kapsamDisi: boolean } & Bicim>;
  toplu: Bicim;
  uretildiginde: string;
}

/*
 * HESAP EKSİK Mİ — ekrandaki `mrpHesabiEksik` DOĞRUDAN kullanılır (bir dönem burada birebir kopyası
 * vardı). true ise HİÇBİR yerde "eksik hammadde yok" DENMEZ: motorun atladığı tek bir kalem bile
 * varsa liste eksiktir. Bir dönem bu kontrol yalnız ekranda vardı; belge, hesabın eksik olduğunu
 * yazdığı hâlde bir alt satırda "eksik hammadde yok" diyordu.
 */

/** Motorun hesaba katamadıkları — baskıda da SUSULMAZ (ekrandaki `MrpUyarilari` ile aynı bilgi). */
function Uyarilar({ v }: { v: Bicim }) {
  const rows: string[] = [];
  if (v.recetesizUrunler.length) {
    rows.push(`Etkin reçetesi olmayan ürünler (ihtiyaç HESAPLANAMADI): ${v.recetesizUrunler.map((x) => `${x.name} (${x.code}) ${nf(x.qty, 2)}`).join(" · ")}`);
  }
  if (v.patlatilamayanBilesenler.length) {
    // Miktar ve GEREKÇE ekranda vardı, baskıda düşüyordu — belgeyle çalışan kişi eksik bilgiyle kalır.
    rows.push(`Patlatılamayan alt bileşenler (altındaki hammaddeler hesaba GİRMEDİ): ${v.patlatilamayanBilesenler
      .map((x) => `${x.name} (${x.code}) ${nf(x.qty, 2)} ${x.uom}${x.recetesiVar ? " — patlatma sınırı: döngü/derinlik" : " — etkin reçetesi yok"}`)
      .join(" · ")}`);
  }
  if (v.atlananKalemler.length) {
    rows.push(`Patlatmaya girmeyen satırlar (ham madde satırının reçetesi olmaz): ${v.atlananKalemler.map((x) => `${x.name} (${x.code})`).join(" · ")}`);
  }
  if (rows.length === 0) return null;
  return (
    <div className="mrp-uyari">
      <b>Bu hesap EKSİK:</b>
      <ul>{rows.map((r, i) => <li key={i}>{r}</li>)}</ul>
    </div>
  );
}

/**
 * "Eldeki" sütununun TANIMI — anlamı değişti (2 Eki 2026: kullanılabilir net stok). Belge Stok
 * ekranıyla yan yana okunur; tanım yazılmazsa iki rakam çelişkili görünür. Metin ekrandaki dipnotla
 * AYNI sabitlerden gelir. 2. ve 3. bölüm ayrı ayrı okunabildiği için ikisinde de basılır.
 */
function EldeAciklamasi() {
  return (
    <p className="aciklama">
      <b>Eldeki</b> = {MRP_ELDE_TANIMI}. <b>Kayıt açığı</b> = {MRP_KAYIT_ACIGI_TANIMI}.
    </p>
  );
}

function EksikTablo({ satirlar }: { satirlar: EksikSatir[] }) {
  return (
    <table className="mrp-t">
      <thead>
        <tr>
          <th>Hammadde</th>
          <th className="r">Brüt İhtiyaç</th>
          <th className="r">Eldeki</th>
          <th className="r">Açık PO</th>
          <th className="r">Eksik</th>
          <th>Tedarikçi</th>
        </tr>
      </thead>
      <tbody>
        {satirlar.map((s) => (
          <tr key={s.itemId}>
            <td>{s.name} <span className="mrp-kod">{s.code}</span></td>
            <td className="r">{nf(s.brut, 2)} {s.uom}</td>
            <td className="r">
              {nf(s.elde, 2)} {s.uom}
              {/* Elde SAYILMAYAN stok + kayıt açığı — yeni sütun yok (A4'e sığsın), hücre altı not.
                  Etiket/eşik ekrandakiyle AYNI kaynaktan (`mrpEldeNotlari`). */}
              {mrpEldeNotlari(s).map((n) => (
                <span key={n.tur} className={`mrp-elde-not ${n.tur}`}>
                  {n.etiket}: <span className="v">{nf(n.qty, 2)} {s.uom}</span>
                </span>
              ))}
            </td>
            <td className="r">{nf(s.acikPo, 2)} {s.uom}</td>
            <td className="r mrp-eksik">{nf(s.eksik, 2)} {s.uom}</td>
            <td>{s.tedarikciAd ?? "—"}{s.tedarikSuresiGun != null ? ` · ${s.tedarikSuresiGun} gün` : ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function MrpReportPrintPage() {
  const [sp] = useSearchParams();
  // Künye — diğer baskı sayfalarıyla aynı desen: belge kimliksiz dosyalanamaz.
  const profQ = trpc.settings.companyProfile.get.useQuery({}, { staleTime: 5 * 60_000 });
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const ben = meQ.data?.user ?? null;
  const hazirlayan = ben ? `${ben.firstName ?? ""} ${ben.lastName ?? ""}`.trim() || ben.email : "—";
  const orderIds = useMemo(
    () => (sp.get("orderIds") ?? "").split(",").map((x) => x.trim()).filter(Boolean),
    [sp],
  );
  const q = trpc.order.materialNeedsMulti.useQuery(
    { orderIds },
    { enabled: orderIds.length > 0, staleTime: 60_000 },
  );
  // Cast YOK — sunucu şekli `Rapor`a uymazsa tsc burada yakalar (bkz. dosya başı TİP SÖZLEŞMESİ).
  const d: Rapor | undefined = q.data;

  return (
    <div className="mrp-page">
      <style>{`
        :root { --ink:#1a2230; --muted:#6b7280; --line:#d7dbe2; --soft:#f4f6f9; --kirmizi:#8b1a1a; }
        .mrp-page { font-family: Montserrat, system-ui, sans-serif; color: var(--ink); max-width: 210mm; margin: 0 auto; padding: 0 0 24px; background:#fff; }
        .mrp-toolbar { position: sticky; top:0; z-index:5; background:#fff; display:flex; gap:12px; align-items:center; justify-content:space-between; padding:10px 24px; border-bottom:1px solid var(--line); }
        .mrp-toolbar .baslik { font-size:13px; font-weight:600; }
        .mrp-toolbar .eylem { display:flex; gap:8px; }
        .mrp-btn { border:1px solid var(--ink); background: var(--ink); color:#fff; font-weight:600; font-size:13px; padding:7px 16px; border-radius:6px; cursor:pointer; }
        .mrp-btn.sec { background:#fff; color: var(--ink); }
        .mrp-btn:disabled { opacity:.5; cursor:default; }
        .mrp-body { padding: 0 24px; }
        .mrp-head { border-bottom:2px solid var(--ink); padding-bottom:8px; margin: 18px 0 14px; }
        .mrp-head .firma { font-size:11px; font-weight:700; letter-spacing:.06em; text-transform:uppercase; color: var(--muted); margin-bottom:2px; }
        .mrp-head h1 { font-size:19px; margin:0; letter-spacing:-.01em; }
        .mrp-head .meta { font-size:11px; color: var(--muted); margin-top:3px; }
        .mrp-sec { margin: 16px 0 0; }
        .mrp-sec h2 { font-size:13px; margin:0 0 6px; padding-bottom:4px; border-bottom:1px solid var(--line); text-transform:uppercase; letter-spacing:.04em; }
        .mrp-sec .aciklama { font-size:10.5px; color: var(--muted); margin: 0 0 7px; line-height:1.5; }
        table.mrp-t { width:100%; border-collapse:collapse; font-size:11px; }
        table.mrp-t th { text-align:left; font-size:9px; text-transform:uppercase; letter-spacing:.05em; color: var(--muted); border-bottom:1px solid var(--line); padding:5px 6px; }
        table.mrp-t td { padding:4px 6px; border-bottom:1px solid #eceff3; vertical-align:top; }
        table.mrp-t .r { text-align:right; font-variant-numeric: tabular-nums; white-space:nowrap; }
        .mrp-kod { color: var(--muted); font-size:9.5px; margin-left:4px; }
        .mrp-eksik { color: var(--kirmizi); font-weight:700; }
        .mrp-sip { border:1px solid var(--line); border-radius:5px; margin-bottom:8px; break-inside: avoid; }
        .mrp-sip-h { display:flex; gap:10px; align-items:baseline; flex-wrap:wrap; background: var(--soft); padding:5px 8px; border-bottom:1px solid var(--line); font-size:11.5px; }
        .mrp-sip-h .kod { font-weight:700; }
        .mrp-sip-h .ek { color: var(--muted); font-size:10.5px; }
        .mrp-sip table.mrp-t { font-size:10.5px; }
        .mrp-sip table.mrp-t th, .mrp-sip table.mrp-t td { padding:3px 8px; }
        /* Sipariş kutusu gövdesi: tablo kenara yaslı, uyarı/bilgi/boş satırı 8px içeride. */
        .mrp-sip-govde > .mrp-uyari { margin-left:8px; margin-right:8px; }
        .mrp-sip-govde > .mrp-bos { padding:8px; }
        /* Elde hücresi altı not: SKT geçmiş / Karantina-QC / Kayıt açığı. Sütun eklenmez (A4'e sığsın);
           .r nowrap'ı burada açılır — etiket sarabilir, miktar (.v) bölünmez. Renk + METİN: s/b baskıda da okunur. */
        .mrp-elde-not { display:block; margin-top:1px; font-size:9px; font-weight:600; line-height:1.35; white-space:normal; color: var(--ink); }
        .mrp-elde-not .v { white-space:nowrap; }
        .mrp-elde-not.acik { color: var(--kirmizi); }
        /* Ekrandaki bilgi kutuları (.mn-msg.info.mn-bilgi — MRP dışı gider kalemleri + "eksik yok ama dikkat")
           baskı görünümüne uyarlanır: belge paleti, ekran rengi yok. */
        .mrp-page .mn-msg.mn-bilgi { display:flex; gap:6px; align-items:flex-start; color: var(--ink); background:#fff; border:1px solid var(--line); border-radius:4px; padding:6px 9px; margin:6px 0; font-size:10.5px; line-height:1.5; break-inside: avoid; }
        .mrp-page .mn-msg.mn-bilgi .mn-bilgi-ack { color: var(--muted); font-size:10px; }
        .mrp-page .mn-msg.mn-bilgi .mn-disi-tur { color: var(--ink); }
        .mrp-page .mn-msg.mn-bilgi .mn-bilgi-liste { color: var(--ink); }
        .mrp-page .mn-msg.mn-bilgi .mn-disi-qty { color: var(--ink); }
        .mrp-page .mn-msg.mn-bilgi .mn-elde-uyari-ad { color: var(--ink); }
        .mrp-page .mn-msg.mn-bilgi .mn-kod { color: var(--muted); font-size:9.5px; }
        /* Kutudaki elde notları tablodaki .mrp-elde-not ile AYNI belge renginde: mürekkep, kayıt açığı kırmızı.
           Bu seçiciler MrpUyarilari.css'teki .mn-elde-not .skt (0,2,0) ekran rengini geçer. */
        .mrp-page .mn-msg.mn-bilgi .mn-elde-not li { color: var(--ink); font-size:9.5px; }
        .mrp-page .mn-msg.mn-bilgi .mn-elde-not li.acik { color: var(--kirmizi); }
        /* Sipariş kutusu içinde de 8px içeride — üstteki margin:6px 0'ı (0,3,0) geçmek için (0,4,0). */
        .mrp-page .mrp-sip-govde > .mn-msg.mn-bilgi { margin-left:8px; margin-right:8px; }
        .mrp-uyari { border:1px solid var(--kirmizi); background:#fdf3f3; color: var(--kirmizi); border-radius:4px; padding:6px 9px; font-size:10.5px; margin:6px 0; }
        .mrp-uyari ul { margin:4px 0 0; padding-left:16px; }
        .mrp-not { border-left:3px solid var(--ink); background: var(--soft); padding:7px 10px; font-size:10.5px; line-height:1.55; margin: 0 0 9px; }
        .mrp-bos { color: var(--muted); font-size:11px; padding:8px 0; }
        .mrp-imza { display:flex; gap:40px; margin-top:22px; font-size:10.5px; color: var(--muted); }
        .mrp-imza div { flex:1; border-top:1px solid var(--line); padding-top:4px; }
        @media print {
          .mrp-toolbar { display:none !important; }
          .mrp-page { max-width:none; }
          .mrp-body { padding:0; }
          .mrp-sec { break-inside: auto; }
          .mrp-sec h2 { break-after: avoid; }
          tr { break-inside: avoid; }
          thead { display: table-header-group; }
        }
        @page { size: A4 portrait; margin: 12mm; }
      `}</style>

      <div className="mrp-toolbar">
        <span className="baslik">MRP Raporu — {orderIds.length} sipariş</span>
        <span className="eylem">
          <button type="button" className="mrp-btn sec" onClick={() => window.close()}>Kapat</button>
          <button type="button" className="mrp-btn" onClick={() => window.print()} disabled={!d || d.yetkiYok}>Yazdır</button>
        </span>
      </div>

      <div className="mrp-body">
        {orderIds.length === 0 ? (
          <div className="mrp-bos">Sipariş seçilmedi. MRP ekranından sipariş seçip raporu yeniden açın.</div>
        ) : q.isLoading ? (
          <div className="mrp-bos">MRP hesaplanıyor…</div>
        ) : q.isError ? (
          <div className="mrp-uyari" role="alert">{q.error.message}</div>
        ) : !d ? null : d.yetkiYok ? (
          <div className="mrp-uyari" role="alert">
            Bu rapor reçete kırılımı içerir — görmek için “Reçete kırılımını görüntüle” (view:recipe) izni gerekir.
          </div>
        ) : (
          <>
            <div className="mrp-head">
              <div className="firma">{profQ.data?.name ?? "STEPERP"}</div>
              <h1>MRP Raporu — Seçili Siparişler</h1>
              <div className="meta">
                {d.siparisler.length} sipariş · {fmtTarih(d.uretildiginde)} tarihinde hesaplandı · Hazırlayan: <b>{hazirlayan}</b>
              </div>
            </div>

            {/* İPTAL siparişler talebe girmez — SESSİZ düşmesin diye en üstte yazılır. */}
            {d.kapsamDisiSiparisler.length > 0 ? (
              <div className="mrp-uyari">
                <b>Kapsam dışı:</b> {d.kapsamDisiSiparisler.map((x) => x.code).join(" · ")} — iptal edilmiş
                oldukları için talebe DAHİL EDİLMEDİ; aşağıdaki eksik listeleri bu siparişleri içermez.
              </div>
            ) : null}

            {/* ── 1) SİPARİŞLER VE KALEMLERİ ── */}
            <div className="mrp-sec">
              <h2>1 · Seçili Siparişler ve Kalemleri</h2>
              <p className="aciklama">
                <b>MRP miktarı</b>, siparişin sevk edilmiş / üretilmiş kısmı düşüldükten sonra üretilecek
                kalan miktardır; hammadde ihtiyacı bu miktar üzerinden hesaplanır.
              </p>
              {d.siparisler.map((s) => (
                <div className="mrp-sip" key={s.id}>
                  <div className="mrp-sip-h">
                    <span className="kod">{s.code}</span>
                    <span>{s.musteri}</span>
                    <span className="ek">{durumEtiketi(s.durum)}</span>
                    <span className="ek">
                      Teslim: {fmtTarih(s.teslimTarihi)}{s.teslimTahmini ? " *" : ""}
                    </span>
                  </div>
                  <table className="mrp-t">
                    <thead>
                      <tr>
                        <th>Ürün</th>
                        <th className="r">Sipariş Miktarı</th>
                        <th className="r">MRP Miktarı</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.kalemler.map((k, i) => (
                        <tr key={`${k.code}-${i}`}>
                          <td>{k.name} <span className="mrp-kod">{k.code}</span></td>
                          <td className="r">{nf(k.siparisMiktar, 0)} {k.uom}</td>
                          {/* `null` = bu ürünün MRP miktarı ÜST satırda yazıldı (ürün bazlı) — çift okunmasın. */}
                          <td className="r">{k.mrpMiktar == null ? "—" : `${nf(k.mrpMiktar, 0)} ${k.uom}`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              {d.siparisler.some((s) => s.teslimTahmini) ? (
                <p className="aciklama">* Söz verilen teslim tarihi girilmemiş; istenen tarih gösteriliyor.</p>
              ) : null}
            </div>

            {/* ── 2) SİPARİŞ BAZINDA EKSİKLER ── */}
            <div className="mrp-sec">
              <h2>2 · Eksik Kalemler — Sipariş Bazında</h2>
              <p className="aciklama">
                Her sipariş <b>TEK BAŞINA</b> üretilecekmiş gibi hesaplanır: eldeki stok her siparişe
                ayrı ayrı tahsis edilmiş sayılır. Bu tablo "yalnız bu siparişi üretsem neyim eksik"
                sorusunu yanıtlar. <b>Satın alma kararı bu tablodan verilmez</b> — 3. bölüme bakın.
              </p>
              <EldeAciklamasi />
              {d.perSiparis.map((p) => (
                <div className="mrp-sip" key={p.orderId}>
                  <div className="mrp-sip-h">
                    <span className="kod">{p.code}</span>
                    <span>{p.musteri}</span>
                    <span className="ek">{p.satirlar.length} eksik kalem</span>
                  </div>
                  <div className="mrp-sip-govde">
                    <Uyarilar v={p} />
                    {/* MRP dışı gider kalemleri + "eksik yok ama dikkat" — ekrandakiyle AYNI bileşen/metin. */}
                    <MrpUyarilari mrpDisiKalemler={p.mrpDisiKalemler} eldeUyarilari={p.eldeUyarilari} />
                    {p.satirlar.length === 0 ? (
                      <div className="mrp-bos">
                        {p.kapsamDisi
                          ? "İptal edilmiş sipariş — talebe dahil edilmedi."
                          : p.talepYok
                            ? "Üretilecek miktar kalmadı (tamamı sevk edilmiş / üretilmiş)."
                            : mrpHesabiEksik(p)
                              ? "Hesaba GİREN kalemlerde eksik çıkmadı — yukarıdaki kalemler katılamadığı için sonuç TAM DEĞİL."
                              // Yeterlilik cümlesi ekranla AYNI kaynaktan; MRP dışı/elde uyarısı varsa daralır.
                              : `Bu sipariş tek başına üretilirse: ${yeterliMetni({ mrpDisiKalemler: p.mrpDisiKalemler, eldeUyarilari: p.eldeUyarilari })}`}
                      </div>
                    ) : (
                      <EksikTablo satirlar={p.satirlar} />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* ── 3) TOPLU EKSİKLER — SATIN ALMA KARARI BURADAN ── */}
            <div className="mrp-sec">
              <h2>3 · Eksik Kalemler — Toplu (Tüm Seçili Siparişler)</h2>
              <div className="mrp-not">
                <b>Bu tablo, 2. bölümdeki eksiklerin toplamı DEĞİLDİR.</b> Stok tüm siparişlerce
                paylaşılır: elde 150 kg un varken iki siparişin her biri 100 kg istiyorsa tek tek
                bakıldığında ikisinde de eksik görünmez, birlikte bakıldığında 50 kg eksik vardır.
                Bu bölüm siparişlerin birleşik talebiyle yapılmış <b>ayrı bir MRP koşusudur</b> ve
                satın alma kararı buradan verilir.
              </div>
              <EldeAciklamasi />
              <Uyarilar v={d.toplu} />
              <MrpUyarilari mrpDisiKalemler={d.toplu.mrpDisiKalemler} eldeUyarilari={d.toplu.eldeUyarilari} />
              {d.toplu.satirlar.length === 0 ? (
                <div className={mrpHesabiEksik(d.toplu) ? "mrp-uyari" : "mrp-bos"}>
                  {d.toplu.hesaplananKalem === 0
                    ? "Hesaplanabilen üretim kalemi yok — eksik listesi ÜRETİLEMEDİ."
                    : mrpHesabiEksik(d.toplu)
                      // ⚠ "Eksik yok" ancak hesap TAM ise söylenir. Motorun atladığı tek kalem
                      // bile varsa liste eksiktir; yeterlilik iddiasında bulunulamaz.
                      ? "Hesaba GİREN kalemlerde eksik çıkmadı — ancak yukarıdaki kalemler hesaba katılamadığı için bu sonuç TAM DEĞİL; yeterlilik iddiasında bulunulamaz."
                      : `Seçili siparişlerin tamamı için: ${yeterliMetni({ mrpDisiKalemler: d.toplu.mrpDisiKalemler, eldeUyarilari: d.toplu.eldeUyarilari })}`}
                </div>
              ) : (
                <EksikTablo satirlar={d.toplu.satirlar} />
              )}
            </div>

            <div className="mrp-imza">
              <div>Hazırlayan</div>
              <div>Kontrol</div>
              <div>Onay</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
